/**
 * Security codes (docs/01 §5, ADR-30, ADR-32): the vault for values that must
 * never appear in a list payload, an export, or a log — door codes, alarm
 * codes, the Federal EIN. The VALUE is absent from every shape here except the
 * reveal response, which exists only as its own audited endpoint.
 *
 * The type enum lives HERE, shared, because it diverged once already: 0015
 * widened the database CHECK to admit `federal_ein` (ADR-32) and the API's own
 * local enum was never widened to match, so the EIN could not actually be
 * stored through the API. One list, imported by both sides, cannot drift.
 */

import { z } from 'zod';

export const SECURITY_CODE_TYPES = [
  'lockbox',
  'vacant_lockbox',
  'roof_hatch',
  'alarm',
  'utility_pin',
  'account_access',
  'federal_ein',
  'other',
] as const;
export const securityCodeTypeSchema = z.enum(SECURITY_CODE_TYPES);
export type SecurityCodeType = z.infer<typeof securityCodeTypeSchema>;

/** Display names, in the order rows sort (the DB orders by code_type). */
export const SECURITY_CODE_TYPE_LABELS: Record<SecurityCodeType, string> = {
  lockbox: 'Lockbox',
  vacant_lockbox: 'Vacant-unit lockbox',
  roof_hatch: 'Roof hatch',
  alarm: 'Alarm',
  utility_pin: 'Utility PIN',
  account_access: 'Account access',
  federal_ein: 'Federal EIN',
  other: 'Other',
};

/**
 * What a code can belong to. `property_group` joins in 0035 (docs/11 D-2): a
 * group's Federal EIN is exactly as sensitive as a property's and belongs in
 * the same vault (ADR-32), and before 0035 the CHECK refused it so the field
 * had nowhere to live at all.
 */
export const securityCodeSubjectSchema = z.enum([
  'property',
  'space',
  'property_group',
  'utility_account',
  'vendor',
]);
export type SecurityCodeSubject = z.infer<typeof securityCodeSubjectSchema>;

/** What the list endpoint returns. No value, ever (docs/01 §5). */
export interface SecurityCodeSummary {
  readonly id: string;
  readonly subjectType: SecurityCodeSubject;
  readonly subjectId: string;
  readonly codeType: SecurityCodeType;
  readonly label: string | null;
  readonly notes: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly updatedAt: string;
}

export const securityCodeCreateSchema = z.object({
  subjectType: securityCodeSubjectSchema,
  subjectId: z.uuid(),
  codeType: securityCodeTypeSchema,
  label: z.string().trim().max(60).nullish(),
  // Codes are short by nature; a generous cap still refuses a pasted document.
  value: z.string().min(1).max(500),
  notes: z.string().trim().max(1000).nullish(),
});
export type SecurityCodeCreate = z.infer<typeof securityCodeCreateSchema>;

export const securityCodeUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: z
    .object({
      value: z.string().min(1).max(500).optional(),
      label: z.string().trim().max(60).nullable().optional(),
      notes: z.string().trim().max(1000).nullable().optional(),
    })
    .refine((changes) => Object.keys(changes).length > 0, 'nothing to change'),
});
